// Team health for the pilot (TEAM-12, TEAM-13, TEAM-14, D-104, D-108, D-118): is each person's phone app working, are
// their calls reaching Voxowa with their recordings, and do its pop-ups show (who is calling while the phone rings,
// "What next?" after a call)? For owners,
// admins and managers. Live: a synced call or an after-call choice refreshes it within seconds,
// and it refreshes every 30 seconds anyway (for check-ins and sign-ins) and whenever the tab comes back into view.
import { useEffect, useRef } from "react";
import { fmtPhone, roleLabel } from "../lib/format";
import {
  ago,
  callsSentAt,
  healthProblems,
  healthTotals,
  type PhoneState,
  phoneState,
  recordingProblems,
} from "../lib/teamHealth";
import { useMember } from "../app/session";
import { Card, Empty, ErrorNote, Loading, Page, Stat, useLoad } from "../app/ui";

const BADGE: Record<PhoneState, string> = {
  on_call: "brand",
  active: "ok",
  quiet: "",
  not_today: "danger",
  no_phone: "danger",
};

export function Health() {
  const { api, member, tz } = useMember();
  const { data, error, loading, reload } = useLoad(() => api.teamHealth(), [api]);

  // The latest reload, so that the timers below are not restarted by every render.
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  useEffect(() => {
    let pending: ReturnType<typeof setTimeout> | undefined;
    // A burst of changes (a sync of many calls) makes one refresh, three seconds after the first.
    const soon = () => {
      pending ??= setTimeout(() => {
        pending = undefined;
        void reloadRef.current();
      }, 3_000);
    };
    const stop = api.onTeamActivity(member.org_id, soon);
    const timer = setInterval(() => void reloadRef.current(), 30_000);
    const onVisible = () => {
      if (document.visibilityState === "visible") void reloadRef.current();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      stop();
      clearInterval(timer);
      if (pending) clearTimeout(pending);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [api, member.org_id]);

  if (loading && !data) return <Loading />;
  if (!data) return <ErrorNote error={error} />;

  const now = new Date(data.now);
  const dayStart = new Date(data.day_start);
  const plan = member.features;
  const t = healthTotals(data.members, plan, now, dayStart);
  // Pop-ups were first counted partway through a day: say from when, that day.
  const countedFrom = (iso: string | null | undefined) =>
    iso && new Date(iso) > dayStart
      ? ` (counted from ${
        new Date(iso).toLocaleTimeString("en-IN", { timeZone: tz, hour: "numeric", minute: "2-digit" })
      })`
      : "";

  return (
    <Page
      title="Team health"
      sub={
        <>
          <span className="badge ok">Live</span> Is everyone's phone app working? Updated{" "}
          {now.toLocaleTimeString("en-IN", { timeZone: tz, hour: "2-digit", minute: "2-digit", second: "2-digit" })}
          , as calls arrive.
        </>
      }
    >
      <ErrorNote error={error} />
      <div className="stack">
        <div className="grid cols-4">
          <Stat
            label="Phones active now"
            value={`${t.active} of ${t.withPhone}`}
            hint="Seen in the last 15 minutes"
            alert={t.withPhone > 0 && t.active === 0}
          />
          <Stat label="Calls, last 15 minutes" value={t.last15} hint={`${t.lastHour} in the last hour`} />
          <Stat
            label="Calls today"
            value={t.today}
            hint={`${t.outgoing} outgoing · ${t.incoming} incoming · ${t.missed} missed`}
          />
          <Stat
            label="People with a problem"
            value={t.withProblems}
            hint={`${t.signinsToday} phone sign-ins today`}
            alert={t.withProblems > 0}
          />
        </div>
        <p className="faint">
          After-call pop-up opened {t.popupsShown} times today, with a choice on {t.popupChoices} calls
          {countedFrom(data.counting?.after_call_since)}. Ringing pop-up on {t.ringingWithPopup} of {t.ringingIncoming}
          {" "}
          incoming calls{countedFrom(data.counting?.ringing_since)}. Recordings for {t.answered - t.unrecorded} of{" "}
          {t.answered} answered calls half an hour old.
        </p>

        <Card title="People" flush>
          {data.members.length === 0 ? <Empty>No one in the team yet.</Empty> : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Phone app</th>
                    <th title="Calls that reached Voxowa">Calls: 15 min · hour · today</th>
                    <th>Today</th>
                    <th title="Answered calls of 10 seconds or more, half an hour old, that have a recording">
                      Recorded
                    </th>
                    <th title='The "What next?" pop-up after a call: how often it opened, and the calls given a choice'>
                      After-call pop-up
                    </th>
                    <th title="Incoming calls, answered or not, that showed who was calling while the phone rang">
                      Ringing pop-up
                    </th>
                    <th title="Sign-ins on a phone today">Sign-ins</th>
                    <th>Needs attention</th>
                  </tr>
                </thead>
                <tbody>
                  {data.members.map((m) => {
                    const state = phoneState(m, now, dayStart);
                    const problems = healthProblems(m, plan);
                    const d = m.device;
                    return (
                      <tr key={m.member_id}>
                        <td className="primary">
                          {m.name}
                          <div className="faint">{roleLabel[m.role]} · {fmtPhone(m.phone)}</div>
                        </td>
                        <td>
                          <span className={`badge ${BADGE[state.state]}`}>{state.text}</span>
                          {d && (
                            <div className="faint">
                              {[d.manufacturer, d.model].filter(Boolean).join(" ")}
                              {d.app_version ? ` · app ${d.app_version}` : ""}
                              {callsSentAt(m) ? ` · calls sent ${ago(callsSentAt(m), now)}` : " · no calls sent yet"}
                            </div>
                          )}
                        </td>
                        <td>
                          {m.calls.last_15_min} · {m.calls.last_hour} · <strong>{m.calls.today}</strong>
                          {m.calls.last_at && <div className="faint">last {ago(m.calls.last_at, now)}</div>}
                        </td>
                        <td className="faint">
                          {m.calls.outgoing} out · {m.calls.incoming} in · {m.calls.missed} missed
                        </td>
                        <td>
                          {m.calls.answered_10s
                            ? (
                              <>
                                {m.calls.answered_10s - (m.calls.unrecorded ?? 0)} of {m.calls.answered_10s}
                                {(m.calls.unrecorded ?? 0) > 0 && (
                                  <div>
                                    <span className={recordingProblems(m).length > 0 ? "badge danger" : "faint"}>
                                      {m.calls.unrecorded} not recorded
                                    </span>
                                  </div>
                                )}
                              </>
                            )
                            : <span className="faint">{m.calls.answered_10s === 0 ? "none yet" : "–"}</span>}
                        </td>
                        <td>
                          {m.after_call
                            ? (
                              <>
                                {m.after_call.shown_today} opened · <strong>{m.after_call.choices}</strong> chosen
                                <div className="faint">{m.after_call.shown_last_hour} in the last hour</div>
                              </>
                            )
                            : `${m.calls.choices} chosen`}
                        </td>
                        <td>
                          {m.ringing && m.ringing.incoming > 0
                            ? (
                              <>
                                {m.ringing.with_popup} of {m.ringing.incoming}
                                <div className="faint">incoming calls</div>
                              </>
                            )
                            : <span className="faint">{m.ringing ? "no incoming calls" : "–"}</span>}
                        </td>
                        <td>
                          {m.signins_today >= 3
                            ? <span className="badge danger">{m.signins_today}</span>
                            : m.signins_today}
                        </td>
                        <td>
                          {problems.length === 0 ? <span className="faint">–</span> : (
                            <ul className="health-problems">
                              {problems.map((p) => <li key={p}>{p}</li>)}
                            </ul>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
        <p className="faint">
          Calls count once the phone has sent them, usually within a minute of the call. A phone that is active but
          shows no calls for a long time, or a person who signs in again and again, needs a look. An answered call with
          no recording, once the phone has no files waiting, was not recorded by the phone itself: check its call
          recording setting.
        </p>
      </div>
    </Page>
  );
}
